import { type Components, type Theme } from "@mui/material/styles";

import { applyPlatformAwareInteractionStyles } from "../../utils/applyPlatformAwareInteractionStyles";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    outlined: true;
    muted: true;
    link: true;
    linkInverted: true;
    outlineInverted: true;
    danger: true;

    // We need to keep this variant because old code uses it
    contained: true;
  }

  interface ButtonPropsSizeOverrides {
    small: true;
    medium: true;
    large: true;
  }
}

export const muiButtonOverrides: Components<Theme> = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        fontWeight: 500,
        textTransform: "none",
        borderRadius: theme.borderRadius?.xLarge,

        "& .MuiButton-endIcon": {
          margin: 0,
        },

        "& .MuiButton-startIcon": {
          margin: 0,
        },

        boxShadow: "none",

        "&:hover": {
          boxShadow: "none",
        },

        "&:active": {
          boxShadow: "none",
        },

        variants: [
          {
            props: { variant: "primary" },
            style: {
              backgroundColor: theme.palette.button?.primary.normal.backgroundColor,
              color: theme.palette.button?.primary.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.primary.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.primary.hover.backgroundColor,
                  color: theme.palette.button?.primary.hover.color,
                  borderColor: theme.palette.button?.primary.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.primary.normal.backgroundColor,
                  color: theme.palette.button?.primary.normal.color,
                  borderColor: theme.palette.button?.primary.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.primary.disabled.backgroundColor,
                color: theme.palette.button?.primary.disabled.color,
                borderColor: theme.palette.button?.primary.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "secondary" },
            style: {
              backgroundColor: theme.palette.button?.secondary.normal.backgroundColor,
              color: theme.palette.button?.secondary.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.secondary.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.secondary.hover.backgroundColor,
                  color: theme.palette.button?.secondary.hover.color,
                  borderColor: theme.palette.button?.secondary.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.secondary.normal.backgroundColor,
                  color: theme.palette.button?.secondary.normal.color,
                  borderColor: theme.palette.button?.secondary.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.secondary.disabled.backgroundColor,
                color: theme.palette.button?.secondary.disabled.color,
                borderColor: theme.palette.button?.secondary.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "outlined" },
            style: {
              backgroundColor: theme.palette.button?.outline.normal.backgroundColor,
              color: theme.palette.button?.outline.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.outline.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.outline.hover.backgroundColor,
                  color: theme.palette.button?.outline.hover.color,
                  borderColor: theme.palette.button?.outline.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.outline.normal.backgroundColor,
                  color: theme.palette.button?.outline.normal.color,
                  borderColor: theme.palette.button?.outline.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.outline.disabled.backgroundColor,
                color: theme.palette.button?.outline.disabled.color,
                borderColor: theme.palette.button?.outline.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "muted" },
            style: {
              backgroundColor: theme.palette.button?.muted.normal.backgroundColor,
              color: theme.palette.button?.muted.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.muted.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.muted.hover.backgroundColor,
                  color: theme.palette.button?.muted.hover.color,
                  borderColor: theme.palette.button?.muted.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.muted.normal.backgroundColor,
                  color: theme.palette.button?.muted.normal.color,
                  borderColor: theme.palette.button?.muted.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.muted.disabled.backgroundColor,
                color: theme.palette.button?.muted.disabled.color,
                borderColor: theme.palette.button?.muted.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "link" },
            style: {
              backgroundColor: theme.palette.button?.link.normal.backgroundColor,
              color: theme.palette.button?.link.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.link.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.link.hover.backgroundColor,
                  color: theme.palette.button?.link.hover.color,
                  borderColor: theme.palette.button?.link.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.link.normal.backgroundColor,
                  color: theme.palette.button?.link.normal.color,
                  borderColor: theme.palette.button?.link.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.link.disabled.backgroundColor,
                color: theme.palette.button?.link.disabled.color,
                borderColor: theme.palette.button?.link.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "linkInverted" },
            style: {
              backgroundColor: theme.palette.button?.link.inverted.normal.backgroundColor,
              color: theme.palette.button?.link.inverted.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.link.inverted.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.link.inverted.hover.backgroundColor,
                  color: theme.palette.button?.link.inverted.hover.color,
                  borderColor: theme.palette.button?.link.inverted.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.link.inverted.normal.backgroundColor,
                  color: theme.palette.button?.link.inverted.normal.color,
                  borderColor: theme.palette.button?.link.inverted.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.link.inverted.disabled.backgroundColor,
                color: theme.palette.button?.link.inverted.disabled.color,
                borderColor: theme.palette.button?.link.inverted.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "outlineInverted" },
            style: {
              backgroundColor: theme.palette.button?.outline.inverted.normal.backgroundColor,
              color: theme.palette.button?.outline.inverted.normal.color,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.button?.outline.inverted.normal.borderColor,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.button?.outline.inverted.hover.backgroundColor,
                  color: theme.palette.button?.outline.inverted.hover.color,
                  borderColor: theme.palette.button?.outline.inverted.hover.borderColor,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.button?.outline.inverted.normal.backgroundColor,
                  color: theme.palette.button?.outline.inverted.normal.color,
                  borderColor: theme.palette.button?.outline.inverted.normal.borderColor,
                },
              }),

              "&:disabled": {
                backgroundColor: theme.palette.button?.outline.inverted.disabled.backgroundColor,
                color: theme.palette.button?.outline.inverted.disabled.color,
                borderColor: theme.palette.button?.outline.inverted.disabled.borderColor,
              },
            },
          },

          {
            props: { variant: "danger" },
            style: {
              backgroundColor: "transparent",
              color: theme.palette.base?.danger,
              borderWidth: theme.borderWidth?.regular,
              borderStyle: "solid",
              borderColor: theme.palette.base?.danger,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: "rgba(221, 23, 23, 0.04)",
                  color: theme.palette.base?.danger,
                  borderColor: theme.palette.base?.danger,
                },
                defaultStyle: {
                  backgroundColor: "transparent",
                  color: theme.palette.base?.danger,
                  borderColor: theme.palette.base?.danger,
                },
              }),

              "&:disabled": {
                backgroundColor: "transparent",
                color: theme.palette.base?.danger,
                borderColor: theme.palette.base?.danger,
                opacity: 0.5,
              },
            },
          },

          {
            props: { size: "small" },
            style: {
              paddingLeft: theme.size?.button.small.paddingX,
              paddingRight: theme.size?.button.small.paddingX,
              paddingTop: theme.size?.button.small.paddingY,
              paddingBottom: theme.size?.button.small.paddingY,
              gap: theme.spacing(2),

              height: theme.size?.button.small.height,
              ...theme.typography.body2,

              // Override icon sizes to match design specs
              "& svg": {
                width: theme.size?.button.small.iconSize,
                height: theme.size?.button.small.iconSize,
              },
            },
          },

          {
            props: { size: "medium" },
            style: {
              paddingLeft: theme.size?.button.medium.paddingX,
              paddingRight: theme.size?.button.medium.paddingX,
              paddingTop: theme.size?.button.medium.paddingY,
              paddingBottom: theme.size?.button.medium.paddingY,
              gap: theme.spacing(3),
              height: theme.size?.button.medium.height,
              ...theme.typography.body1,

              // Override icon sizes to match design specs
              "& svg": {
                width: theme.size?.button.medium.iconSize,
                height: theme.size?.button.medium.iconSize,
              },
            },
          },

          {
            props: { size: "large" },
            style: {
              paddingLeft: theme.size?.button.large.paddingX,
              paddingRight: theme.size?.button.large.paddingX,
              paddingTop: theme.size?.button.large.paddingY,
              paddingBottom: theme.size?.button.large.paddingY,
              gap: theme.spacing(3),
              height: theme.size?.button.large.height,
              ...theme.typography.body1,

              // Override icon sizes to match design specs
              "& svg": {
                width: theme.size?.button.large.iconSize,
                height: theme.size?.button.large.iconSize,
              },
            },
          },
        ],
      }),
    },
  },
};
